import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Seo, ORG_JSONLD } from "../lib/seo";
import { EASE, MaskedLines, Reveal, Chapter, ParallaxImg } from "../lib/motion";
import { BreakCard } from "../components/BreakCard";
import { EmailCapture } from "../components/EmailCapture";
import { speLink, trackOutbound } from "../lib/track";
import { SearchWidget } from "../components/SearchWidget";

const SPE = "https://www.stayplayexplore.co.uk";
const spimg = (uuid, size = "1000x.jpg") => `${SPE}/images/files/${uuid}/${size}`;

function PriceStamp() {
  return (
    <motion.div
      className="relative h-32 w-32 sm:h-40 sm:w-40"
      initial={{ opacity: 0, scale: 0.7, rotate: -20 }}
      animate={{ opacity: 1, scale: 1, rotate: 0 }}
      transition={{ duration: 1.2, ease: EASE, delay: 1.3 }}
      data-testid="hero-price-stamp"
    >
      <svg viewBox="0 0 100 100" className="h-full w-full animate-rotate-slow">
        <defs>
          <path id="tpb-circle" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
        </defs>
        <text className="fill-sand text-[7.6px] font-semibold uppercase" style={{ letterSpacing: "0.18em" }}>
          <textPath href="#tpb-circle">From £169 for two • hotel + breakfast + 3 experiences •</textPath>
        </text>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="font-serif text-xl font-semibold text-sand sm:text-2xl">£169</span>
        <span className="text-[9px] font-semibold uppercase tracking-[0.2em] text-sand/80">for two</span>
      </div>
    </motion.div>
  );
}


const WHY = [
  { title: "More than just a hotel", copy: "Every break includes three experiences you actually choose — not just a room and a hope for the best." },
  { title: "Hand-picked for adults", copy: "Hotels and experiences chosen for grown-up getaways: heritage, food, drink, rivers, galleries, gardens." },
  { title: "One honest price for two", copy: "From £169 for two people — not per person. No per-person pricing games, no nasty surprises." },
  { title: "Flexible & easy", copy: "Weekend or midweek, flexible dates, extra nights when you want them. Booked in minutes." },
];

const STEPS = [
  { title: "Choose your dates", copy: "Pick a start date and how many nights — or stay flexible to see the widest availability." },
  { title: "Choose your hotel", copy: "See the hand-picked hotels available for your dates and pick your favourite." },
  { title: "Pick your experiences", copy: "Choose any three from a hand-picked local collection. They're included." },
  { title: "Pack your bags", copy: "Hotel, breakfast and experiences sorted in one simple price for two." },
];

const COUNTIES = ["Leicestershire", "Coventry", "Warwickshire", "Northamptonshire"];

export default function Home() {
  const [site, setSite] = useState(null);
  const [breaks, setBreaks] = useState([]);
  const [experiences, setExperiences] = useState([]);

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
    api.get("/breaks").then((r) => setBreaks(r.data.filter((b) => b.featured))).catch(() => {});
    api.get("/experiences").then((r) => setExperiences(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="homepage">
      <Seo
        title="The Perfect Break | UK Short Breaks for Adults — Hotel + 3 Experiences from £169 for two"
        description="Great-value UK short breaks for adults in the Heart of England. Hand-picked hotels with breakfast plus your choice of three experiences, from £169 for two."
        path="/"
        jsonLd={ORG_JSONLD}
      />

      {/* HERO — kinetic, masked reveal, parallax */}
      <section className="relative overflow-hidden bg-primary" data-testid="hero-section">
        {site?.hero_image ? (
          <ParallaxImg
            src={site.hero_image}
            alt="A Perfect Break in the Heart of England"
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <div className="absolute inset-0 h-full w-full bg-primary" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/25" />
        <div className="container-tpb relative flex min-h-[100svh] flex-col justify-end pb-14 pt-36 sm:pb-20">
          <motion.p
            className="overline !text-sand/90"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
            data-testid="hero-overline"
          >
            UK short breaks for adults
          </motion.p>
          <h1 className="mt-5 max-w-4xl font-serif text-5xl font-medium leading-[1.0] tracking-tight text-sand sm:text-7xl lg:text-8xl" style={{ textShadow: "0 2px 30px rgba(0,0,0,0.45)" }} data-testid="hero-title">
            <MaskedLines
              immediate
              delay={0.3}
              lines={[
                { text: "Your perfect" },
                { text: "UK break.", className: "italic" },
                { text: "Your way." },
              ]}
            />
          </h1>
          <motion.p
            className="mt-6 max-w-xl text-base leading-relaxed text-sand/90 sm:text-lg"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.0 }}
            style={{ textShadow: "0 1px 12px rgba(0,0,0,0.5)" }}
            data-testid="hero-sub"
          >
            {site?.hero_sub}
          </motion.p>
          <motion.div
            className="mt-9 flex flex-wrap items-center gap-6"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.2 }}
          >
            <Link to="/breaks" className="btn-accent group !px-8 !py-4 !text-base" data-testid="hero-find-break-button">
              Find your perfect break <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <PriceStamp />
          </motion.div>
          <motion.div
            className="absolute bottom-14 right-5 hidden flex-col items-center gap-3 sm:right-12 lg:flex"
            data-testid="hero-scroll-cue"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 1 }}
            aria-hidden="true"
          >
            <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-sand/70" style={{ writingMode: "vertical-rl" }}>Scroll</span>
            <motion.span className="h-14 w-px bg-sand/50" animate={{ scaleY: [1, 0.4, 1] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: "top" }} />
          </motion.div>
          <motion.p
            className="absolute bottom-24 right-4 max-w-[46vw] text-right text-[11px] leading-relaxed text-sand/70 sm:bottom-20 sm:right-6 sm:max-w-xs lg:right-28"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.0, duration: 1 }}
            style={{ textShadow: "0 1px 10px rgba(0,0,0,0.6)" }}
            data-testid="hero-image-caption"
          >
            <a
              href="https://www.stayplayexplore.co.uk/attractions/compton-verney"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold uppercase tracking-[0.14em] underline-offset-2 transition-colors duration-300 hover:text-sand hover:underline"
              data-testid="hero-image-credit-link"
            >
              Compton Verney, Warwickshire
            </a>
            {" "}— one of the amazing places you can experience on your Perfect Break
          </motion.p>
        </div>
      </section>

      {/* DATE SEARCH — collects dates here, hands off to SPE results */}
      <section className="container-tpb relative z-20 -mt-8 sm:-mt-12" data-testid="home-search-section">
        <Reveal>
          <SearchWidget placement="home-search" />
        </Reveal>
      </section>

      {/* ONE slow editorial marquee */}
      {site?.marquee && (
        <div className="overflow-hidden border-y border-sandline bg-sand-dark py-4" aria-hidden="true">
          <div className="flex w-max animate-marquee gap-12">
            {[...site.marquee, ...site.marquee].map((item, i) => (
              <span key={i} className="flex items-center gap-12 whitespace-nowrap font-serif text-xl italic text-primary">
                {item} <span className="text-accent not-italic">✳</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 01 — HOW IT WORKS (dates first) */}
      <section className="container-tpb py-24 sm:py-36" data-testid="how-it-works-section">
        <Chapter num="№ 01" overline="How it works" title={["Easier than planning", "it yourself"]} />
        <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <div data-testid={`step-${i + 1}`}>
                <span className="font-serif text-6xl font-medium leading-none text-sandline">{i + 1}</span>
                <h3 className="mt-4 font-serif text-2xl font-semibold text-ink">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">{s.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 02 — FEATURED BREAKS */}
      <section className="border-y border-sandline bg-white/50 py-24 sm:py-32" data-testid="featured-breaks-section">
        <div className="container-tpb">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <Chapter num="№ 02" overline="The breaks" title="Ready when you are" />
            <Reveal delay={0.2}>
              <Link to="/breaks" className="btn-ghost group" data-testid="see-all-breaks-link">
                See all breaks <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-16 grid gap-x-8 gap-y-14 sm:grid-cols-2">
            {breaks.map((b, i) => <BreakCard key={b.slug} brk={b} index={i} />)}
          </div>
        </div>
      </section>

      {/* 03 — EXPERIENCES (dark editorial interlude) */}
      <section className="bg-primary py-24 text-sand sm:py-36" data-testid="experiences-section">
        <div className="container-tpb">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <Chapter dark num="№ 03" overline="The difference" title={["The experiences", "are included"]} />
            <Reveal delay={0.2}>
              <Link to="/experiences" className="group inline-flex items-center gap-2 rounded-full border border-sand/40 px-6 py-3 text-sm font-semibold text-sand transition-colors duration-300 hover:bg-sand hover:text-primary" data-testid="explore-experiences-link">
                Explore the experiences <Compass className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="mt-10 max-w-2xl font-serif text-2xl italic leading-snug text-sand/90 sm:text-3xl" data-testid="experiences-statement">
              “Anyone can book a room. We include the part you remember.”
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-sm font-semibold uppercase tracking-[0.2em] text-sand/70" data-testid="experiences-count-line">
              Choose any three — {experiences.length} experiences and counting
            </p>
          </Reveal>
          <div className="relative mt-8">
            <div className="flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid sm:grid-cols-3 sm:gap-6 sm:overflow-visible sm:pb-0" data-testid="home-experience-scroller">
              {experiences.slice(0, 6).map((e, i) => (
                <a
                  key={e.name}
                  href={speLink(e.spe_url, `home-${e.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackOutbound({ break_slug: null, destination_url: e.spe_url, placement: "home-experience" })}
                  className="group w-[64vw] shrink-0 snap-start sm:w-auto"
                  data-testid={`experience-tile-${i}`}
                >
                  <figure>
                    <div className="aspect-[4/3] overflow-hidden rounded-lg">
                      <img src={e.image} alt={e.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.07]" />
                    </div>
                    <figcaption className="mt-3">
                      <span className="font-serif text-lg font-semibold leading-tight text-sand transition-colors duration-300 group-hover:text-white">{e.name}</span>
                      <span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.25em] text-sand/60">{e.type} · {e.destination_name}</span>
                    </figcaption>
                  </figure>
                </a>
              ))}
              <Link
                to="/experiences"
                className="group flex w-[64vw] shrink-0 snap-start items-center justify-center rounded-lg border border-sand/30 transition-colors duration-300 hover:bg-sand/10 sm:w-auto"
                data-testid="experience-tile-see-all"
              >
                <span className="px-6 text-center font-serif text-xl italic text-sand">
                  See all {experiences.length} experiences <ArrowRight className="mt-1 inline h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </div>
            <p className="mt-4 text-xs text-sand/60 sm:hidden" data-testid="experiences-swipe-hint">Swipe for more — this is just a taste of the collection</p>
          </div>
        </div>
      </section>

      {/* 04 — WHY (manifesto) */}
      <section className="container-tpb py-24 sm:py-36" data-testid="why-tpb-section">
        <Chapter num="№ 04" overline="Why The Perfect Break" title={["Built for adults who'd", "rather be away than planning"]} />
        <div className="mt-16">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.06}>
              <div className="group grid gap-3 border-t border-sandline py-9 transition-[padding] duration-500 hover:pl-4 sm:grid-cols-12 sm:items-baseline sm:gap-8" data-testid={`why-${i}`}>
                <span className="font-serif text-lg italic text-accent sm:col-span-2">№ {String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif text-2xl font-medium tracking-tight text-ink sm:col-span-5 sm:text-4xl">{w.title}</h3>
                <p className="text-sm leading-relaxed text-ink-muted sm:col-span-5">{w.copy}</p>
              </div>
            </Reveal>
          ))}
          <div className="border-t border-sandline" />
        </div>
      </section>

      {/* 05 — HEART OF ENGLAND (single destination) */}
      <section className="border-y border-sandline bg-white/50 py-24 sm:py-32" data-testid="destinations-section">
        <div className="container-tpb grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Chapter num="№ 05" overline="Where to" title={["One region.", "A hundred stories."]} />
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-xl leading-relaxed text-ink-muted">
                All our breaks live in the Heart of England — close to home, far from ordinary.
              </p>
              <div className="mt-6 flex flex-wrap gap-2" data-testid="home-county-chips">
                {COUNTIES.map((c) => (
                  <span key={c} className="rounded-full border border-sandline bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-primary">{c}</span>
                ))}
              </div>
              <Link to="/heart-of-england" className="btn-ghost group mt-8" data-testid="explore-heart-of-england-link">
                Explore the Heart of England <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <Link to="/heart-of-england" className="group relative block overflow-hidden rounded-lg" data-testid="destination-card-heart-of-england">
              <div className="aspect-[16/11] overflow-hidden">
                <img src={spimg("019e666c-dd54-4a71-be53-af248819adf8", "original.jpg")} alt="Compton Verney in the Heart of England" loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 flex w-full items-end justify-between p-7">
                <span className="font-serif text-2xl font-semibold text-sand sm:text-3xl" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.5)" }}>The Heart of England</span>
                <ArrowRight className="h-6 w-6 text-sand transition-transform duration-300 group-hover:translate-x-1.5" />
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 06 — EMAIL */}
      <section className="container-tpb py-24 sm:py-36" data-testid="email-section">
        <div className="mx-auto max-w-2xl text-center">
          <Chapter num="№ 06" overline="Don't miss a break" title={["Be first to discover", "new Perfect Breaks"]} className="[&_p]:justify-center [&_h2]:text-center" />
          <Reveal delay={0.2}>
            <p className="mt-6 leading-relaxed text-ink-muted">New destinations, seasonal breaks and subscriber-first offers. No noise, unsubscribe anytime.</p>
            <div className="mt-9 text-left">
              <EmailCapture location="homepage" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-primary py-24 text-center sm:py-32" data-testid="final-cta-section">
        <div className="container-tpb">
          <h2 className="mx-auto max-w-3xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-sand sm:text-6xl">
            <MaskedLines lines={["Your perfect break is", { text: "one decision away", className: "italic" }]} />
          </h2>
          <Reveal delay={0.3}>
            <p className="mx-auto mt-5 max-w-md text-sand/80">Hotel, breakfast and three experiences. From £169 for two.</p>
            <Link to="/breaks" className="btn-accent group mt-9 !px-9 !py-4 !text-base" data-testid="final-cta-button">
              Find your perfect break <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <p className="mx-auto mt-4 max-w-md text-xs text-sand/60" data-testid="final-cta-reassurance">
              Secure booking is completed through our established Stay Play Explore booking system.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
